import React, { useState, useEffect } from 'react'
import { 
  TrendingUp,
  ShoppingCart,
  Package,
  DollarSign,
  Calendar,
  Filter,
  Download
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import purchaseAnalysisService from '../../services/purchaseAnalysisService'
import DataTable from '../../components/DataTable/DataTable'

const PurchaseAnalysis = () => {
  const navigate = useNavigate()
  const [analysisData, setAnalysisData] = useState(null)
  const [filterOptions, setFilterOptions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    supplier: '',
    category: '',
    status: ''
  })

  useEffect(() => {
    loadFilterOptions()
    loadAnalysisData()
  }, [])

  useEffect(() => {
    loadAnalysisData()
  }, [filters])

  const loadFilterOptions = async () => {
    try {
      const response = await purchaseAnalysisService.getFilterOptions()
      if (response.success) {
        setFilterOptions(response.data)
        // Set default date range (last 12 months)
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)
        setFilters(prev => ({
          ...prev,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }))
      }
    } catch (error) {
      console.error('Error loading filter options:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load filter options')
      }
    }
  }

  const loadAnalysisData = async () => {
    try {
      setLoading(true)
      const response = await purchaseAnalysisService.getPurchaseAnalysis(filters)
      if (response.success) {
        setAnalysisData(response.data)
      } else {
        toast.error(response.message || 'Failed to load purchase analysis')
      }
    } catch (error) {
      console.error('Error loading purchase analysis:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load purchase analysis')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleResetFilters = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 12)
    setFilters({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      supplier: '',
      category: '',
      status: ''
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0)
  }

  // Summary Cards
  const summaryCards = analysisData ? [
    {
      title: 'Total Orders',
      subtitle: 'In selected period',
      value: formatNumber(analysisData.overallStats.totalOrders),
      icon: ShoppingCart,
      cardClass: 'deals',
      trend: 'positive',
      trendLabel: 'Orders'
    },
    {
      title: 'Total Amount',
      subtitle: 'Purchase spend',
      value: formatCurrency(analysisData.overallStats.totalAmount),
      icon: DollarSign,
      cardClass: 'revenue',
      trend: 'positive',
      trendLabel: 'Spend'
    },
    {
      title: 'Average Order Value',
      subtitle: 'Per order',
      value: formatCurrency(analysisData.overallStats.averageOrderValue),
      icon: TrendingUp,
      cardClass: 'conversion',
      trend: 'neutral',
      trendLabel: 'AOV'
    },
    {
      title: 'Total Items',
      subtitle: 'Line items ordered',
      value: formatNumber(analysisData.overallStats.totalItems),
      icon: Package,
      cardClass: 'churn',
      trend: 'neutral',
      trendLabel: 'Items'
    }
  ] : []

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Reports', path: '/procurement?tab=reports' },
    { label: 'Purchase Analysis', isCurrent: true }
  ]

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  // Category Analysis Table Columns
  const categoryColumns = [
    {
      accessorKey: 'categoryName',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', fontWeight: '500' }}>
          {getValue() || 'Uncategorized'}
        </div>
      ),
    },
    {
      accessorKey: 'orderCount',
      header: 'Orders',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total Quantity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageUnitPrice',
      header: 'Avg. Unit Price',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  // Supplier Analysis Table Columns
  const supplierColumns = [
    {
      accessorKey: 'supplierName',
      header: 'Supplier',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.supplierCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.supplierCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'orderCount',
      header: 'Orders',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageOrderValue',
      header: 'Avg. Order Value',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  // Product/Service Analysis Table Columns
  const productServiceColumns = [
    {
      accessorKey: 'itemName',
      header: 'Item',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.itemCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.itemCode}</div>
          )}
          <div style={{ fontSize: '12px', color: '#222448' }}>
            {row.original.itemType}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'orderCount',
      header: 'Orders',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantity',
      header: 'Total Quantity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total Amount',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageUnitPrice',
      header: 'Avg. Unit Price',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Purchase Analysis</h1>
          <p>Purchase trends and category analysis</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={18} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Filters</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Status
            </label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                background: '#fff'
              }}
            >
              <option value="">All Statuses</option>
              {filterOptions?.statuses?.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading analysis data...</p>
        </div>
      ) : analysisData ? (
        <>
          <div className="dashboard-metrics-grid">
            {summaryCards.map((card, index) => {
              const Icon = card.icon
              return (
                <div key={index} className={`metric-card ${card.cardClass}`}>
                  <div className="metric-header">
                    <div className="metric-icon">
                      <Icon size={16} />
                    </div>
                    <div className={`metric-trend ${card.trend}`}>
                      <span>{card.trendLabel}</span>
                    </div>
                  </div>
                  <div className="metric-value">{card.value}</div>
                  <div className="metric-title">{card.title}</div>
                  <div className="metric-subtitle">{card.subtitle}</div>
                </div>
              )
            })}
          </div>

          {/* Monthly Trends Chart Placeholder */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Purchase Trends (Last 12 Months)</h3>
            <div style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f9fafb',
              borderRadius: '6px',
              color: 'var(--color-text-secondary, #5B6478)'
            }}>
              Chart visualization would be displayed here
              <br />
              <small>Monthly order count and total amount trends</small>
            </div>
          </div>

          {/* Category Analysis */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Category Analysis</h3>
            <div className="data-table-section">
              <DataTable
                data={analysisData.categoryAnalysis || []}
                columns={categoryColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                className="purchase-analysis-category-table"
                pageSize={10}
              />
            </div>
          </div>

          {/* Supplier Analysis */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Top Suppliers</h3>
            <div className="data-table-section">
              <DataTable
                data={analysisData.supplierAnalysis || []}
                columns={supplierColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                className="purchase-analysis-supplier-table"
                pageSize={10}
              />
            </div>
          </div>

          {/* Product/Service Analysis */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Top Products/Services</h3>
            <div className="data-table-section">
              <DataTable
                data={analysisData.productServiceAnalysis || []}
                columns={productServiceColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                className="purchase-analysis-product-table"
                pageSize={10}
              />
            </div>
          </div>

          {/* Status Breakdown */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Status Breakdown</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {analysisData.statusBreakdown?.map((status, index) => (
                <div
                  key={index}
                  style={{
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px solid #e9ecef'
                  }}
                >
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginBottom: '8px' }}>{status._id || 'Unknown'}</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)', marginBottom: '4px' }}>
                    {formatNumber(status.count)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>
                    {formatCurrency(status.totalAmount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary, #5B6478)' }}>
          No data available
        </div>
      )}
    </div>
  )
}

export default PurchaseAnalysis

